// Shared URL builders for eBay reference IDs shown in the conversation
// header — one place for each pattern instead of duplicating template
// strings at every InfoField call site (mirrors the backend's own
// CARRIER_URL convention for tracking links).

// The real listing page — prefers the URL captured at ingest time
// (conversation.metadata.listing.url) over building one from the bare
// listing ID, since the captured URL may carry extra query params eBay
// itself included.
export function ebayListingUrl(listingValue, metadataUrl) {
  return metadataUrl ?? `https://www.ebay.com/itm/${listingValue}`
}

export function ebayReturnDetailsUrl(returnId) {
  return `https://www.ebay.com/rt/ReturnDetails?returnId=${returnId}`
}

// An eBay case or inquiry, by where its id came from (the contact card's
// rule, one place for it; 2026-10-28 the refund steps link with it too):
//   'inquiry' | 'casemanagement' | 'case'  an INR inquiry, or a case found
//       through Post-Order casemanagement: the ItemNotReceived page
//   anything else (an escalated return's case, an unresolved id): the
//       ReturnCase page
export function ebayCaseUrl(caseId, kind = null) {
  return kind === 'inquiry' || kind === 'casemanagement' || kind === 'case'
    ? `https://www.ebay.com/ItemNotReceived/${caseId}`
    : `https://www.ebay.com/ReturnCase/${caseId}`
}

// The id on an eBay refund step's route (2026-10-29, the step card's
// "Refund on eBay return <id> ..." line, EbayRefundRouteNote.jsx): a return
// to its Return Details page, an inquiry or a case by ebayCaseUrl()'s rule.
// null for the seller refund (no id).
export function ebayRefundRouteUrl(route) {
  if (route?.id == null || route.id === '') return null
  if (route.via === 'return' || route.kind === 'return') return ebayReturnDetailsUrl(route.id)
  return ebayCaseUrl(route.id, route.kind ?? (route.via === 'inquiry' ? 'inquiry' : 'casemanagement'))
}

// The "Refund on the return / inquiry" button for a stopped refund step
// (2026-10-29): the label when the step's Process stopped because an eBay
// return or INR inquiry is open on the order, else null (a case has no
// CXHub refund step).
export function ebayRerouteButtonLabel(stepState) {
  const r = stepState?.ebayRefundReroute
  if (!r || (r.via !== 'return' && r.via !== 'inquiry')) return null
  return r.label ?? (r.via === 'return' ? 'Refund on the return' : 'Refund on the inquiry')
}

// The seller refund steps the Process pre-check can stop (2026-10-29).
const SELLER_REFUND_STEP_TYPES = ['create_ebay_refund', 'process_return_refund', 'issue_partial_return_refund_ebay']

// A stopped refund's state after a reload (2026-10-29): the executor keeps
// a CXHub note on the step's Activity Log (ebayRefundStop, with the step's
// error and the route's kind and id); the rebuild endpoint's note
// (ebayRefundRerouted) or a later refund that went through settles it. Only
// for a seller refund step still at that index.
export function ebayRerouteStepStates(suggestion) {
  const calls = suggestion?.modelMeta?.apiCalls ?? []
  const actions = suggestion?.proposedActions ?? []
  const states = {}
  for (const c of calls) {
    if (!Number.isInteger(c?.stepIndex)) continue
    if (c.ebayRefundStop && c.ebayRefundReroute) {
      states[c.stepIndex] = { status: 'failed', result: null, error: c.stepError ?? c.label ?? 'Action failed', ebayRefundReroute: c.ebayRefundReroute }
    } else if (c.ebayRefundRerouted || (!c.error && c.label === 'issueOrderRefund')) {
      delete states[c.stepIndex]
    }
  }
  for (const k of Object.keys(states)) {
    if (!SELLER_REFUND_STEP_TYPES.includes(actions[Number(k)]?.type)) delete states[k]
  }
  return states
}
